!function(){
  // ==== Configuration ====
  const CONFIG = {
    TEST_ID: "SB-083DM",              // Unique test ID (used for logging & style ID)
    TEST_NAME: "Cart Notification Reminder",         // Descriptive test name
    BUCKET: "Variation 1",             // A/B variant (e.g., A, B, CONTROL)

    STYLES: {
      FONT: "font-weight: 900; font-size: 11px",
      SUCCESS: "background:rgb(21, 83, 65); color: #FFFFFF",
      WARNING: "background:rgb(251, 200, 174); color: #F62817"
    },

    SELECTORS: {
      BODY: "body",
      TARGET: "main#MainContent",
    },

    TIMEOUT: 30000,                // Max wait time for selectors/variables
    INTERVAL: 25,                  // Interval for polling checks
    OBSERVER: false                 // Toggle MutationObserver logic
  };

  // ==== Styled Console Logging ====
  const STYLE = CONFIG.STYLES;
  const log = {
    success: (msg) => console.log(`%c ${msg} `, `${STYLE.SUCCESS};${STYLE.FONT}`),
    warning: (msg) => console.warn(`%c ${msg} `, `${STYLE.WARNING};${STYLE.FONT}`),

    start() {
      this.success(`==== ${CONFIG.TEST_ID} START ====`);
      this.success(`ID: ${CONFIG.TEST_ID} \n NAME: ${CONFIG.TEST_NAME} \n BUCKET: ${CONFIG.BUCKET} `);
    },
    end() {
      this.success(`==== ${CONFIG.TEST_ID} END ====`);
    },
    fail(error) {
      this.warning(`%c ERROR => ${error.message}`, `${STYLE.WARNING};${STYLE.FONT}`);
      this.warning(`%c ${CONFIG.TEST_ID} x x x x x Failed`, `${STYLE.WARNING};${STYLE.FONT}`);
    }
  };

  // ==== Test bucket log ====
  console.log(`%c [${CONFIG.TEST_ID}] Bucketed into: ${CONFIG.BUCKET}`, `${STYLE.SUCCESS};${STYLE.FONT}`);

  // ==== Utility Functions ====
  const utils = {
    q: (s, e = document) => e.querySelector(s),
    qs: (s, e = document) => e.querySelectorAll(s),
    eh: (el, pos, html) => el.insertAdjacentHTML(pos, html),
    ie: (el, pos, node) => el.insertAdjacentElement(pos, node),

    wait(selector, cb, {
      multiple = false,
      isVariable = false,
      minCount = 1,
      timeout = CONFIG.TIMEOUT,
      interval = CONFIG.INTERVAL,
      el = document
    } = {}) {
      const start = Date.now();
      (function check() {
        const result = isVariable
          ? window[selector]
          : (multiple ? utils.qs(selector, el) : utils.q(selector, el));

        const isReady = isVariable
          ? typeof result !== 'undefined'
          : (multiple ? result.length >= minCount : !!result);

        if (isReady) cb(result);
        else if (Date.now() - start < timeout) setTimeout(check, interval);
      })();
    },

    debounce(fn, delay) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    isMobile() {
      return window.matchMedia("(max-width: 767px)").matches;
    },

    // Check if element (el) is in viewport (any part visible)
    isInViewport(el) {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;
      return (
        rect.top < windowHeight &&
        rect.bottom > 0 &&
        rect.left < windowWidth &&
        rect.right > 0
      );
    },

    // Observer-based wait for element to appear
    waitForElement(selector, cb, {
      timeout = CONFIG.TIMEOUT,
      el = document
    } = {}) {
      // Check if element already exists
      const existingElement = utils.q(selector, el);
      if (existingElement) {
        cb(existingElement);
        return null; // Return null since no observer was created
      }

      const startTime = Date.now();
      const observer = new MutationObserver(() => {
        const element = utils.q(selector, el);
        if (element) {
          observer.disconnect();
          cb(element);
        } else if (Date.now() - startTime >= timeout) {
          observer.disconnect();
          // Optional: call callback with null if timeout reached
          // cb(null);
        }
      });

      observer.observe(el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden'] // Watch for aria-hidden changes
      });

      // Set timeout fallback
      setTimeout(() => {
        observer.disconnect();
      }, timeout);

      return observer; // Return observer so it can be disconnected if needed
    }
  };

  // ==== Main Test Logic ====
  const test = {
    init() {
      this.main();        // Run main test logic
      this.eventHandlers(); // Run event handlers
      if (CONFIG.OBSERVER) this.observeMutations(); // Optional DOM observer
    },

    main() {
      let checkMobile = utils.isMobile();
      if(checkMobile)
      {
        console.log("mobile view load");
        return;
      }
      else{
        console.log("desktop view load");
      }

      const notificationShownKey = `${CONFIG.TEST_ID}_notification_shown`;
      const notificationClosedKey = `${CONFIG.TEST_ID}_notification_closed`;
      const notificationActiveKey = `${CONFIG.TEST_ID}_notification_active`;
      const notificationShown = sessionStorage.getItem(notificationShownKey);
      const notificationClosed = sessionStorage.getItem(notificationClosedKey);
      const notificationActive = sessionStorage.getItem(notificationActiveKey);
      if (notificationShown === 'true' && notificationClosed === 'true') return; // Don't show again in this session
      
      // Check if timer was already started (persisted across page loads)
      const timerStartKey = `${CONFIG.TEST_ID}_timer_start`;
      const existingTimerStart = sessionStorage.getItem(timerStartKey);
      const DELAY_MS = 60000; // 60 seconds
      const self = this;
      
      const isCartDrawerVisible = () => {
        const cartDrawer = utils.q('cart-drawer.active div#CartDrawer');
        return utils.isInViewport(cartDrawer);
      };

      const getCartSnapshot = () => fetch('/cart.json', { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null);

      const waitForCartItems = (cb, {
        timeout = CONFIG.TIMEOUT,
        interval = CONFIG.INTERVAL
      } = {}) => {
        const start = Date.now();
        (function check() {
          getCartSnapshot().then((cart) => {
            if (cart && cart.item_count > 0) {
              cb(cart);
            } else if (Date.now() - start < timeout) {
              setTimeout(check, interval);
            }
          });
        })();
      };
      
      const removeNotificationIfCartOpen = () => {
        if (!isCartDrawerVisible()) return false;
        const notification = utils.q('.r-cart-notification');
        if (notification) {
          notification.remove();
          sessionStorage.setItem(notificationClosedKey, 'true');
          sessionStorage.removeItem(notificationActiveKey);
          self.cartSuppressed = true;
        }
        return true;
      };
      
      const ensureCartObserver = () => {
        if (self.cartObserver) return;
        const observer = new MutationObserver(() => {
          // Only act if notification exists
          if (!utils.q('.r-cart-notification')) return;
          if (isCartDrawerVisible()) {
            utils.q('.r-cart-notification').remove();
            sessionStorage.setItem(notificationClosedKey, 'true');
            sessionStorage.removeItem(notificationActiveKey);
            observer.disconnect();
            self.cartObserver = null;
          }
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true
        });
        self.cartObserver = observer;
      };
      
      // If timer exists, check if 60 seconds have already passed
      if (existingTimerStart) {
        const elapsed = Date.now() - parseInt(existingTimerStart, 10);
        const remaining = DELAY_MS - elapsed;
        
        if (remaining <= 0) {
          // 60 seconds have already passed, show notification immediately
          // Double check if notification already shown (race condition protection)
          const alreadyShown = sessionStorage.getItem(notificationShownKey);
          if (alreadyShown === 'true') {
            sessionStorage.removeItem(timerStartKey); // Clean up
            return;
          }
          if (isCartDrawerVisible()) {
            sessionStorage.setItem(notificationShownKey, 'true');
            sessionStorage.removeItem(timerStartKey); // Clean up
            removeNotificationIfCartOpen();
            return;
          }
          getCartSnapshot().then((cart) => {
            if (!cart || cart.item_count <= 0) return;
            sessionStorage.setItem(notificationShownKey, 'true');
            sessionStorage.removeItem(timerStartKey); // Clean up
            this.addCartNotification(cart.items && cart.items[0] ? cart.items[0].image : null);
          });
          return;
        } else {
          // Timer is still running, wait for remaining time
          ensureCartObserver();
          setTimeout(() => {
            const stillShown = sessionStorage.getItem(notificationShownKey);
            if (stillShown === 'true') return;
            if (isCartDrawerVisible()) {
              sessionStorage.setItem(notificationShownKey, 'true');
              sessionStorage.removeItem(timerStartKey); // Clean up
              removeNotificationIfCartOpen();
              return;
            }
            getCartSnapshot().then((cart) => {
              if (!cart || cart.item_count <= 0) return;
              sessionStorage.setItem(notificationShownKey, 'true');
              sessionStorage.removeItem(timerStartKey); // Clean up
              this.addCartNotification(cart.items && cart.items[0] ? cart.items[0].image : null);
            });
          }, remaining);
          return;
        }
      }
      
      // Observe for cart items to appear via cart.json
      waitForCartItems((cartData) => {
        if (!cartData || cartData.item_count <= 0) return;
        
        // Check again if timer was started while waiting (in case of rapid page loads)
        const checkTimerStart = sessionStorage.getItem(timerStartKey);
        if (checkTimerStart) {
          const elapsed = Date.now() - parseInt(checkTimerStart, 10);
          const remaining = DELAY_MS - elapsed;
          
          if (remaining <= 0) {
            // 60 seconds have already passed
            // Double check if notification already shown (race condition protection)
            const alreadyShown = sessionStorage.getItem(notificationShownKey);
            if (alreadyShown === 'true') {
              sessionStorage.removeItem(timerStartKey);
              return;
            }
            if (isCartDrawerVisible()) {
              sessionStorage.setItem(notificationShownKey, 'true');
              sessionStorage.removeItem(timerStartKey);
              removeNotificationIfCartOpen();
              return;
            }
            sessionStorage.setItem(notificationShownKey, 'true');
            sessionStorage.removeItem(timerStartKey);
            this.addCartNotification(cartData.items && cartData.items[0] ? cartData.items[0].image : null);
            return;
          } else {
            // Wait for remaining time
            ensureCartObserver();
            setTimeout(() => {
              const stillShown = sessionStorage.getItem(notificationShownKey);
              if (stillShown === 'true') return;
              if (isCartDrawerVisible()) {
                sessionStorage.setItem(notificationShownKey, 'true');
                sessionStorage.removeItem(timerStartKey);
                removeNotificationIfCartOpen();
                return;
              }
              getCartSnapshot().then((cart) => {
                if (!cart || cart.item_count <= 0) return;
                sessionStorage.setItem(notificationShownKey, 'true');
                sessionStorage.removeItem(timerStartKey);
                this.addCartNotification(cart.items && cart.items[0] ? cart.items[0].image : null);
              });
            }, remaining);
            return;
          }
        }
        
        if (notificationShown === 'true' && notificationActive === 'true' && notificationClosed !== 'true') {
          ensureCartObserver();
          if (isCartDrawerVisible()) {
            removeNotificationIfCartOpen();
            return;
          }
          if (!self.cartSuppressed) {
            this.addCartNotification(cartData.items && cartData.items[0] ? cartData.items[0].image : null);
          }
          return;
        }
        
        // Start new timer - save timestamp to sessionStorage
        const timerStart = Date.now();
        sessionStorage.setItem(timerStartKey, timerStart.toString());
        ensureCartObserver();
        
        // Wait 60 seconds before showing notification
        setTimeout(() => {
          // Double check if notification already shown (in case user navigated)
          const stillShown = sessionStorage.getItem(notificationShownKey);
          if (stillShown === 'true') return;
          if (isCartDrawerVisible()) {
            sessionStorage.setItem(notificationShownKey, 'true');
            sessionStorage.removeItem(timerStartKey); // Clean up
            removeNotificationIfCartOpen();
            return;
          }
          getCartSnapshot().then((cart) => {
            if (!cart || cart.item_count <= 0) return;
            // Mark as shown in session storage
            sessionStorage.setItem(notificationShownKey, 'true');
            sessionStorage.removeItem(timerStartKey); // Clean up
            // Show the notification
            this.addCartNotification(cart.items && cart.items[0] ? cart.items[0].image : null);
          });
        }, DELAY_MS);
      });

      if (sessionStorage.getItem(timerStartKey) && removeNotificationIfCartOpen()) {
        // If cart is open during the countdown, remove any visible notification
      }
    },

    addCartNotification(imageUrl) {
      const notificationClosedKey = `${CONFIG.TEST_ID}_notification_closed`;
      const notificationActiveKey = `${CONFIG.TEST_ID}_notification_active`;
      const defaultImageSrc = "https://www.transparentlabs.com/cdn/shop/files/TL_WPI_30S_MC_1_3_i1.png";
      const imageSrc = imageUrl || defaultImageSrc;
      utils.wait(CONFIG.SELECTORS.TARGET, (target) => {
        if(!utils.q('.r-cart-notification')) {
          utils.eh(target, "beforeend", `<div class="r-cart-notification">
            <div class="r-cn-main">
              <div class="r-cn-img">
                <img src="${imageSrc}" alt="Cart Notification Image">
              </div>
              <div class="r-cn-details">
                <div class="r-cn-title">Ready to reach your goals?</div>
                <div class="r-cn-description">You’re almost there. Complete your purchase while they’re still in stock.</div>
                <div class="r-cn-button">
                  <div class="r-cn-button-text">View Cart</div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <g clip-path="url(#clip0_55_12172)">
                      <path d="M6.11959e-07 9.5L16 9.5M16 9.5L11.75 13.5M16 9.5L11.75 5.5" stroke="#111314" stroke-width="1.28571" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_55_12172">
                        <rect width="18" height="18" fill="white" transform="translate(18) rotate(90)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
            <div class="r-cn-close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clip-path="url(#clip0_55_12175)">
                  <path d="M5.98953 5.98959L18.0103 18.0104M18.0103 5.98959L5.98953 18.0104" stroke="#111314" stroke-linecap="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_55_12175">
                    <rect width="24" height="24" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>`);
          sessionStorage.removeItem(notificationClosedKey);
          sessionStorage.setItem(notificationActiveKey, 'true');
        }
      });
    },

    eventHandlers() {
      const notificationClosedKey = `${CONFIG.TEST_ID}_notification_closed`;
      const notificationActiveKey = `${CONFIG.TEST_ID}_notification_active`;
      window.addEventListener('click', (e) => {
        const target = e.target;
        if(target.closest('.r-cn-close')) {
          utils.q('.r-cart-notification').remove();
          sessionStorage.setItem(notificationClosedKey, 'true');
          sessionStorage.removeItem(notificationActiveKey);
        }
        if(target.closest('.r-cn-button') && utils.q('a#cart-icon-bubble')) {
          utils.q('a#cart-icon-bubble').click();
          utils.q('.r-cart-notification').remove();
          sessionStorage.setItem(notificationClosedKey, 'true');
          sessionStorage.removeItem(notificationActiveKey);
        }
      });
    },

    observeMutations() {
      const observer = new MutationObserver(utils.debounce(() => {
        this.main();
      }, 300));

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true
      });

      this.observer = observer;
    },

    destroy() {
      if (this.observer) this.observer.disconnect();
      const styleEl = document.getElementById(CONFIG.TEST_ID);
      if (styleEl) styleEl.remove();
    }
  };

  // ==== Bootstrapping the Test ====
  utils.wait(CONFIG.SELECTORS.BODY, (body) => {
    try {
      log.start();   // Log bucket and test info
      body.classList.add(CONFIG.TEST_ID);
      test.init();   // Run test
      log.end();     // Log completion
    } catch (err) {
      log.fail(err); // Log any error
    }
  });
}();
